import React, { useState, useEffect } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import {
  getCustomersList,
  getCustomerMetrics,
  getAllCustomersMetrics,
  generateInsights,
} from '../../utils/customerMetricsApi';
import { safeToFixed, safeToLocaleString } from '../../utils/numberUtils';

// Types for our metrics data
interface MetricsData {
  customerId: string;
  timestamp: string;
  metrics: CustomerMetrics;
  lastUpdated: string;
  sourceFile: string;
  dataSummary?: DataSummary;
}

// Type for customer metrics
interface CustomerMetrics {
  weekly_active_users_growth?: {
    recent_weeks_data: ActiveWeekData[];
    average_growth_rate: number;
    latest_week_growth: number;
  };
  weekly_paid_users_trend?: {
    recent_weeks_data: PaidWeekData[];
    average_growth_rate: number;
    latest_week_growth: number;
  };
  weekly_entitled_users_trend?: {
    recent_weeks_data: EntitledWeekData[];
    average_growth_rate: number;
    latest_week_growth: number;
  };
  active_t4w_trend?: {
    recent_weeks_data: any[];
    latest_t4w_count: number;
  };
  p0_metrics?: {
    paid_entitled_ratio: number;
    active_paid_ratio: number;
    entitled_eligible_ratio: number;
    totals: {
      paid: number;
      entitled: number;
      active: number;
      eligible: number;
    };
  };
  summary?: {
    total_records: number;
    ide_pro_records: number;
    unique_active_users: number;
    total_entitled_users: number;
    total_paid_users: number;
  };
}

// Type for data summary
interface DataSummary {
  total_records: number;
  unique_customers: number;
  ide_pro_tier_records: number;
  date_range: {
    start: string;
    end: string;
  };
}

// Types for week data
interface ActiveWeekData {
  week: string;
  active_users: number;
  growth_rate?: number;
}

interface PaidWeekData {
  week: string;
  paid_users: number;
  growth_rate?: number;
}

interface EntitledWeekData {
  week: string;
  entitled_users: number;
  growth_rate?: number;
}

const QNavCustomerMetrics: React.FC = () => {
  const [selectedCustomer, setSelectedCustomer] = useState<string>(''); // Start empty
  const [customers, setCustomers] = useState<string[]>([]);
  const [metricsData, setMetricsData] = useState<MetricsData | null>(null);
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  // State for customer performance data (for trends summary)
  const [customerPerformance, setCustomerPerformance] = useState<any[]>([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [showAllCustomersTable, setShowAllCustomersTable] = useState(false);

  // Get the list of customers from the API
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customersList = await getCustomersList();

        // Sort customers to put ALL_CUSTOMERS first
        const sortedCustomers = customersList.sort((a, b) => {
          if (a === 'ALL_CUSTOMERS') return -1;
          if (b === 'ALL_CUSTOMERS') return 1;
          return a.localeCompare(b);
        });

        setCustomers(sortedCustomers);

        // Auto-select based on user role
        if (sortedCustomers.includes('ALL_CUSTOMERS')) {
          // SUPER_USER: default to ALL_CUSTOMERS
          setSelectedCustomer('ALL_CUSTOMERS');
        } else if (sortedCustomers.length > 0) {
          // Regular user: default to first customer
          setSelectedCustomer(sortedCustomers[0]);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };

    fetchCustomers();
  }, []);

  // Fetch metrics when selected customer changes
  useEffect(() => {
    const fetchMetrics = async () => {
      if (!selectedCustomer) return;

      setDataLoading(true);
      try {
        let metrics;
        if (selectedCustomer === 'ALL_CUSTOMERS') {
          metrics = await getAllCustomersMetrics();
        } else {
          metrics = await getCustomerMetrics(selectedCustomer);
        }

        setMetricsData(metrics);

        // Generate insights
        generateInsights(metrics);
      } catch (error) {
        console.error('Error fetching metrics:', error);
        // Clear previous data when there's an error
        setMetricsData(null);
      } finally {
        setDataLoading(false);
      }
    };

    fetchMetrics();
  }, [selectedCustomer]);

  // Fetch customer performance data for trends summary
  useEffect(() => {
    const fetchCustomerPerformance = async () => {
      if (selectedCustomer !== 'ALL_CUSTOMERS' || !customers || customers.length <= 1) {
        setCustomerPerformance([]);
        return;
      }

      setPerformanceLoading(true);
      try {
        const individualCustomers = customers.filter((c) => c !== 'ALL_CUSTOMERS');

        // Parallel API calls instead of sequential
        const customerPromises = individualCustomers.map(async (customer) => {
          try {
            const metrics = await getCustomerMetrics(customer);
            if (metrics && metrics.metrics) {
              const paidGrowth = metrics.metrics.weekly_paid_users_trend?.latest_week_growth || 0;

              // Calculate T4W growth for active users (this is what should be shown in T4W Active Users column)
              const t4wData = metrics.metrics.active_t4w_trend?.recent_weeks_data || [];
              const activeGrowth =
                t4wData.length >= 2
                  ? (((t4wData[t4wData.length - 1]?.active_t4w_count || t4wData[t4wData.length - 1]?.active_t4w || 0) -
                      (t4wData[t4wData.length - 2]?.active_t4w_count || t4wData[t4wData.length - 2]?.active_t4w || 0)) /
                      Math.max(
                        t4wData[t4wData.length - 2]?.active_t4w_count || t4wData[t4wData.length - 2]?.active_t4w || 1,
                        1,
                      )) *
                    100
                  : 0;

              const entitledGrowth = metrics.metrics.weekly_entitled_users_trend?.latest_week_growth || 0;
              const paidUsers =
                metrics.metrics.weekly_paid_users_trend?.recent_weeks_data?.slice(-1)[0]?.paid_users || 0;
              const activeUsers =
                metrics.metrics.weekly_active_users_growth?.recent_weeks_data?.slice(-1)[0]?.active_users || 0;
              const entitledUsers =
                metrics.metrics.weekly_entitled_users_trend?.recent_weeks_data?.slice(-1)[0]?.entitled_users || 0;
              const t4wUsers = metrics.metrics.active_t4w_trend?.latest_t4w_count || 0;

              // Enablement metrics
              const paidEntitledRatio = metrics.metrics.p0_metrics?.paid_entitled_ratio || 0;
              const activePaidRatio = metrics.metrics.p0_metrics?.active_paid_ratio || 0;
              const entitledEligibleRatio = metrics.metrics.p0_metrics?.entitled_eligible_ratio || 0;

              return {
                name: customer,
                paidGrowth: Number(paidGrowth),
                activeGrowth: Number(activeGrowth),
                entitledGrowth: Number(entitledGrowth),
                t4wGrowth: Number(activeGrowth), // Same as activeGrowth since both represent T4W growth
                paidUsers: Number(paidUsers),
                activeUsers: Number(activeUsers),
                entitledUsers: Number(entitledUsers),
                t4wUsers: Number(t4wUsers),
                paidEntitledRatio: Number(paidEntitledRatio),
                activePaidRatio: Number(activePaidRatio),
                entitledEligibleRatio: Number(entitledEligibleRatio),
                lastUpdated: metrics.lastUpdated,
              };
            }
            return null;
          } catch (error) {
            console.error(`Failed to fetch metrics for ${customer}:`, error);
            return null;
          }
        });

        // Wait for all parallel requests to complete
        const results = await Promise.all(customerPromises);
        const customerData = results.filter((result) => result !== null);

        setCustomerPerformance(customerData);
      } catch (error) {
        console.error('Error fetching customer performance data:', error);
      } finally {
        setPerformanceLoading(false);
      }
    };

    fetchCustomerPerformance();
  }, [selectedCustomer, customers]);

  // Function to refresh metrics data
  const refreshMetrics = async () => {
    setDataLoading(true);
    try {
      let metrics;
      if (selectedCustomer === 'ALL_CUSTOMERS') {
        metrics = await getAllCustomersMetrics();
      } else {
        metrics = await getCustomerMetrics(selectedCustomer);
      }

      setMetricsData(metrics);

      // Generate insights
      generateInsights(metrics);
    } catch (error) {
      console.error('Error refreshing metrics:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // Function to handle customer selection
  const handleCustomerChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const customer = event.target.value;
    setSelectedCustomer(customer);
  };

  // Function to render trends summary for All Customers
  const renderExecutiveSummary = () => {
    if (selectedCustomer !== 'ALL_CUSTOMERS' || !customers || customers.length <= 1) {
      return null;
    }

    if (performanceLoading) {
      return (
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-secondary-800 mb-6">This Week's Trends</h2>
          <div className="flex items-center justify-center py-12">
            <div className="text-secondary-500">Loading customer performance data...</div>
          </div>
        </div>
      );
    }

    // Sort customers by paid growth (top 3 and bottom 3) - only customers with paid users > 200
    const customersWithSignificantPaidUsers = customerPerformance.filter((customer) => customer.paidUsers > 200);
    const sortedByPaidGrowth = [...customersWithSignificantPaidUsers].sort((a, b) => b.paidGrowth - a.paidGrowth);
    const topPaidGrowth = sortedByPaidGrowth.slice(0, 3);
    const bottomPaidGrowth = sortedByPaidGrowth.slice(-3).reverse(); // Reverse to show worst first

    // Sort customers by active growth (top 3 and bottom 3)
    const sortedByActiveGrowth = [...customerPerformance].sort((a, b) => b.activeGrowth - a.activeGrowth);
    const topActiveGrowth = sortedByActiveGrowth.slice(0, 3);
    const bottomActiveGrowth = sortedByActiveGrowth.slice(-3).reverse(); // Reverse to show worst first

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-secondary-800 mb-6">This Week's Trends</h2>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Top 3 Paid Users Growth */}
          <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
            <h3 className="text-lg font-semibold text-blue-700 mb-4 flex items-center bg-blue-50 p-3 rounded-lg">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Top 3 - Paid Users WoW% Growth
            </h3>
            {topPaidGrowth.map((customer, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-3 border-b border-secondary-100 last:border-b-0"
              >
                <div>
                  <div className="font-medium text-secondary-800">
                    {index + 1}. {customer.name}
                  </div>
                  <div className="text-sm text-secondary-600">{customer.paidUsers} paid users</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600">+{customer.paidGrowth.toFixed(1)}%</div>
                  <div className="text-xs text-secondary-500">this week</div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom 3 Paid Users WoW% Growth */}
          <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
            <h3 className="text-lg font-semibold text-blue-700 mb-4 flex items-center bg-blue-50 p-3 rounded-lg">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Bottom 3 - Paid Users WoW% Growth
            </h3>
            {bottomPaidGrowth.map((customer, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-3 border-b border-secondary-100 last:border-b-0"
              >
                <div>
                  <div className="font-medium text-secondary-800">
                    {index + 1}. {customer.name}
                  </div>
                  <div className="text-sm text-secondary-600">{customer.paidUsers} paid users</div>
                </div>
                <div className="text-right">
                  <div className={`text-lg font-bold ${customer.paidGrowth < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                    {customer.paidGrowth >= 0 ? '+' : ''}
                    {customer.paidGrowth.toFixed(1)}%
                  </div>
                  <div className="text-xs text-secondary-500">this week</div>
                </div>
              </div>
            ))}
          </div>

          {/* Top 3 Active Users Growth */}
          <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
            <h3 className="text-lg font-semibold text-blue-700 mb-4 flex items-center bg-blue-50 p-3 rounded-lg">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Top 3 - Active Users WoW% Growth
            </h3>
            {topActiveGrowth.map((customer, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-3 border-b border-secondary-100 last:border-b-0"
              >
                <div>
                  <div className="font-medium text-secondary-800">
                    {index + 1}. {customer.name}
                  </div>
                  <div className="text-sm text-secondary-600">{customer.activeUsers} active users</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600">+{customer.activeGrowth.toFixed(1)}%</div>
                  <div className="text-xs text-secondary-500">this week</div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom 3 Active Users Growth */}
          <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
            <h3 className="text-lg font-semibold text-blue-700 mb-4 flex items-center bg-blue-50 p-3 rounded-lg">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Bottom 3 - Active Users WoW% Growth
            </h3>
            {bottomActiveGrowth.map((customer, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-3 border-b border-secondary-100 last:border-b-0"
              >
                <div>
                  <div className="font-medium text-secondary-800">
                    {index + 1}. {customer.name}
                  </div>
                  <div className="text-sm text-secondary-600">{customer.activeUsers} active users</div>
                </div>
                <div className="text-right">
                  <div className={`text-lg font-bold ${customer.activeGrowth < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                    {customer.activeGrowth >= 0 ? '+' : ''}
                    {customer.activeGrowth.toFixed(1)}%
                  </div>
                  <div className="text-xs text-secondary-500">this week</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Individual Customers Table */}
        <div className="mt-8">
          <div
            className="flex items-center mb-6 cursor-pointer"
            onClick={() => setShowAllCustomersTable(!showAllCustomersTable)}
          >
            <h3 className="text-xl font-semibold text-gray-800">All Customers Data</h3>
            <svg
              className={`w-5 h-5 ml-2 transform transition-transform duration-200 text-gray-600 ${showAllCustomersTable ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>

          {showAllCustomersTable && (
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200 resize-x overflow-hidden"
                        style={{ minWidth: '120px' }}
                      >
                        Customer
                      </th>
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200 resize-x overflow-hidden"
                        style={{ minWidth: '140px' }}
                      >
                        Entitled Users
                      </th>
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200 resize-x overflow-hidden"
                        style={{ minWidth: '120px' }}
                      >
                        Paid Users
                      </th>
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200 resize-x overflow-hidden"
                        style={{ minWidth: '120px' }}
                      >
                        Active Users
                      </th>
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200 resize-x overflow-hidden"
                        style={{ minWidth: '140px' }}
                      >
                        T4W Active Users
                      </th>
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200 resize-x overflow-hidden"
                        style={{ minWidth: '120px' }}
                      >
                        <div className="flex flex-col">
                          <span className="text-black">Paid/Entitled</span>
                          <span className="text-xs font-normal text-black mt-1">Target: ≥65%</span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200 resize-x overflow-hidden"
                        style={{ minWidth: '120px' }}
                      >
                        <div className="flex flex-col">
                          <span className="text-black">Active/Paid</span>
                          <span className="text-xs font-normal text-black mt-1">Target: ≥75%</span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider resize-x overflow-hidden"
                        style={{ minWidth: '140px' }}
                      >
                        <div className="flex flex-col">
                          <span className="text-black">Entitled/Eligible</span>
                          <span className="text-xs font-normal text-black mt-1">Target: ≥80%</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {customerPerformance
                      .sort((a, b) => (b.entitledUsers || 0) - (a.entitledUsers || 0))
                      .map((customer, index) => (
                        <tr
                          key={index}
                          className="hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100"
                        >
                          <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                            <button
                              onClick={() => setSelectedCustomer(customer.name)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors duration-150"
                            >
                              {customer.name}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                            <div className="text-sm font-medium text-gray-900">
                              {safeToLocaleString(customer.entitledUsers || 0)}
                              <span
                                className={`ml-2 text-xs font-medium px-2 py-1 rounded ${customer.entitledGrowth >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                              >
                                {customer.entitledGrowth >= 0 ? '+' : ''}
                                {safeToFixed(customer.entitledGrowth, 1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                            <div className="text-sm font-medium text-gray-900">
                              {safeToLocaleString(customer.paidUsers)}
                              <span
                                className={`ml-2 text-xs font-medium px-2 py-1 rounded ${customer.paidGrowth >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                              >
                                {customer.paidGrowth >= 0 ? '+' : ''}
                                {safeToFixed(customer.paidGrowth, 1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                            <div className="text-sm font-medium text-gray-900">
                              {safeToLocaleString(customer.activeUsers)}
                              <span
                                className={`ml-2 text-xs font-medium px-2 py-1 rounded ${customer.activeGrowth >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                              >
                                {customer.activeGrowth >= 0 ? '+' : ''}
                                {safeToFixed(customer.activeGrowth, 1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                            <div className="text-sm font-medium text-gray-900">
                              {safeToLocaleString(customer.t4wUsers || 0)}
                              <span
                                className={`ml-2 text-xs font-medium px-2 py-1 rounded ${customer.t4wGrowth >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                              >
                                {customer.t4wGrowth >= 0 ? '+' : ''}
                                {safeToFixed(customer.t4wGrowth, 1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                            <div
                              className={`text-base font-semibold cursor-help ${customer.paidEntitledRatio >= 65 ? 'text-green-600' : 'text-red-600'}`}
                              title={`Paid/Entitled: ${customer.paidUsers} ÷ ${customer.entitledUsers} = ${safeToFixed(customer.paidEntitledRatio, 1)}%`}
                            >
                              {safeToFixed(customer.paidEntitledRatio, 1)}%
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                            <div
                              className={`text-base font-semibold cursor-help ${customer.activePaidRatio >= 75 ? 'text-green-600' : 'text-red-600'}`}
                              title={`Active/Paid: ${customer.activeUsers} ÷ ${customer.paidUsers} = ${safeToFixed(customer.activePaidRatio, 1)}%`}
                            >
                              {safeToFixed(customer.activePaidRatio, 1)}%
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div
                              className={`text-base font-semibold cursor-help ${customer.entitledEligibleRatio >= 80 ? 'text-green-600' : customer.entitledEligibleRatio === 0 ? 'text-gray-400' : 'text-red-600'}`}
                              title={
                                customer.entitledEligibleRatio === 0
                                  ? 'No data available'
                                  : `Entitled/Eligible: ${customer.entitledUsers} ÷ eligible = ${safeToFixed(customer.entitledEligibleRatio, 1)}%`
                              }
                            >
                              {customer.entitledEligibleRatio === 0
                                ? 'N/A'
                                : `${safeToFixed(customer.entitledEligibleRatio, 1)}%`}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Function to render charts
  const renderCharts = () => {
    if (!metricsData || !metricsData.metrics) {
      return null;
    }

    const metrics = metricsData.metrics;

    // Get weekly data
    const activeUsersData = metrics.weekly_active_users_growth?.recent_weeks_data || [];
    const paidUsersData = metrics.weekly_paid_users_trend?.recent_weeks_data || [];
    const entitledUsersData = metrics.weekly_entitled_users_trend?.recent_weeks_data || [];
    const activeT4WData = metrics.active_t4w_trend?.recent_weeks_data || [];

    // Debug logging
    console.log('Active Users Data:', activeUsersData);
    console.log('Paid Users Data:', paidUsersData);

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Weekly Active Users */}
        <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
          <h3 className="text-xl font-semibold text-secondary-800 mb-4">Weekly Active Users</h3>
          {activeUsersData.length > 0 ? (
            <div>
              {/* Simple Bar Chart */}
              <div style={{ padding: '20px 0' }}>
                <div
                  style={{
                    height: '200px',
                    position: 'relative',
                    borderBottom: '2px solid #e5e7eb',
                    borderLeft: '2px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'end',
                    paddingLeft: '10px',
                    paddingBottom: '10px',
                    gap: '4px',
                  }}
                >
                  {activeUsersData.map((week, index) => {
                    const maxValue = Math.max(...activeUsersData.map((w) => Number(w.active_users)));
                    const height = maxValue > 0 ? (Number(week.active_users) / maxValue) * 180 : 0;
                    const isDecline =
                      index > 0 && Number(week.active_users) < Number(activeUsersData[index - 1].active_users);

                    return (
                      <div
                        key={index}
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          height: '100%',
                          justifyContent: 'end',
                          position: 'relative',
                        }}
                      >
                        {/* Number above bar */}
                        <div
                          style={{
                            position: 'absolute',
                            bottom: `${height + 5}px`,
                            fontSize: '10px',
                            color: '#374151',
                            fontWeight: '600',
                          }}
                        >
                          {week.active_users}
                        </div>

                        {/* Bar */}
                        <div
                          style={{
                            width: '100%',
                            height: `${height}px`,
                            backgroundColor: isDecline ? '#ef4444' : '#2563eb',
                            minHeight: '4px',
                            borderRadius: '2px 2px 0 0',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* Week labels */}
                <div style={{ display: 'flex', marginTop: '10px', paddingLeft: '10px', gap: '4px' }}>
                  {activeUsersData.map((week, index) => (
                    <div
                      key={index}
                      style={{
                        flex: 1,
                        fontSize: '11px',
                        color: '#666',
                        textAlign: 'center',
                      }}
                    >
                      {week.week.split('-')[1]}/{week.week.split('-')[2]}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                <div>Avg Growth: {safeToFixed(metrics.weekly_active_users_growth?.average_growth_rate)}%</div>
                <div>Latest: {activeUsersData[activeUsersData.length - 1]?.active_users || 0} users</div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p>No data available</p>
            </div>
          )}
        </div>

        {/* Paid Users Trend */}
        <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
          <h3 className="text-xl font-semibold text-secondary-800 mb-4">Paid Users Trend</h3>
          {paidUsersData.length > 0 ? (
            <div>
              {/* Simple Line Chart using CSS */}
              <div style={{ padding: '20px 0' }}>
                <div
                  style={{
                    height: '200px',
                    position: 'relative',
                    borderBottom: '2px solid #e5e7eb',
                    borderLeft: '2px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'end',
                    paddingLeft: '10px',
                    paddingBottom: '10px',
                  }}
                >
                  {paidUsersData.map((week, index) => {
                    const maxValue = Math.max(...paidUsersData.map((w) => Number(w.paid_users)));
                    const height = maxValue > 0 ? (Number(week.paid_users) / maxValue) * 180 : 0;
                    const isDecline =
                      index > 0 && Number(week.paid_users) < Number(paidUsersData[index - 1].paid_users);

                    return (
                      <div
                        key={index}
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          height: '100%',
                          justifyContent: 'end',
                          position: 'relative',
                        }}
                      >
                        {/* Connecting line to next point */}
                        {index < paidUsersData.length - 1 && (
                          <div
                            style={{
                              position: 'absolute',
                              top: `${180 - height}px`,
                              right: '-50%',
                              width: '100%',
                              height: '3px',
                              background: (() => {
                                const nextValue = Number(paidUsersData[index + 1].paid_users);
                                const currentValue = Number(week.paid_users);
                                if (nextValue < currentValue) return '#ef4444'; // red for decline
                                return '#2563eb'; // blue for growth/stable (same as bg-primary-600)
                              })(),
                              transformOrigin: 'left center',
                              transform: (() => {
                                const nextValue = Number(paidUsersData[index + 1].paid_users);
                                const nextHeight = maxValue > 0 ? (nextValue / maxValue) * 180 : 0;
                                const angle = Math.atan2(height - nextHeight, 100) * (180 / Math.PI);
                                return `rotate(${angle}deg)`;
                              })(),
                              zIndex: 1,
                            }}
                          />
                        )}

                        {/* Data point */}
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: isDecline ? '#ef4444' : '#2563eb',
                            border: '2px solid white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            position: 'absolute',
                            bottom: `${height}px`,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                          }}
                        >
                          {/* Value label */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '-25px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              fontSize: '10px',
                              color: '#374151',
                              fontWeight: '600',
                              backgroundColor: 'white',
                              padding: '2px 4px',
                              borderRadius: '3px',
                              border: '1px solid #e5e7eb',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {week.paid_users}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Week labels */}
                <div style={{ display: 'flex', marginTop: '10px', paddingLeft: '10px' }}>
                  {paidUsersData.map((week, index) => (
                    <div
                      key={index}
                      style={{
                        flex: 1,
                        fontSize: '11px',
                        color: '#666',
                        textAlign: 'center',
                      }}
                    >
                      {week.week.split('-')[1]}/{week.week.split('-')[2]}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                <div>Avg Growth: {safeToFixed(metrics.weekly_paid_users_trend?.average_growth_rate)}%</div>
                <div>Latest: {paidUsersData[paidUsersData.length - 1]?.paid_users || 0} users</div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p>No data available</p>
            </div>
          )}
        </div>

        {/* Entitled Users Trend */}
        <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
          <h3 className="text-xl font-semibold text-secondary-800 mb-4">Entitled Users Trend</h3>
          {entitledUsersData.length > 0 ? (
            <div>
              {/* Simple Area Chart using CSS */}
              <div style={{ padding: '20px 0' }}>
                <div
                  style={{
                    height: '200px',
                    position: 'relative',
                    borderBottom: '2px solid #e5e7eb',
                    borderLeft: '2px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'end',
                    paddingLeft: '10px',
                    paddingBottom: '10px',
                  }}
                >
                  {entitledUsersData.map((week, index) => {
                    const maxValue = Math.max(...entitledUsersData.map((w) => Number(w.entitled_users)));
                    const height = maxValue > 0 ? (Number(week.entitled_users) / maxValue) * 180 : 0;
                    const isDecline =
                      index > 0 && Number(week.entitled_users) < Number(entitledUsersData[index - 1].entitled_users);

                    return (
                      <div
                        key={index}
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          height: '100%',
                          justifyContent: 'end',
                          position: 'relative',
                        }}
                      >
                        {/* Area fill - vertical column with light blue */}
                        <div
                          style={{
                            width: '100%',
                            height: `${height}px`,
                            backgroundColor: isDecline ? 'rgba(239, 68, 68, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                            position: 'relative',
                            zIndex: 1,
                          }}
                        />

                        {/* Connecting line to next point */}
                        {index < entitledUsersData.length - 1 && (
                          <div
                            style={{
                              position: 'absolute',
                              top: `${180 - height}px`,
                              right: '-50%',
                              width: '100%',
                              height: '2px',
                              background: isDecline ? '#ef4444' : '#2563eb',
                              transformOrigin: 'left center',
                              transform: (() => {
                                const nextValue = Number(entitledUsersData[index + 1].entitled_users);
                                const nextHeight = maxValue > 0 ? (nextValue / maxValue) * 180 : 0;
                                const angle = Math.atan2(height - nextHeight, 100) * (180 / Math.PI);
                                return `rotate(${angle}deg)`;
                              })(),
                              zIndex: 2,
                            }}
                          />
                        )}

                        {/* Data point */}
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: isDecline ? '#ef4444' : '#2563eb',
                            border: '2px solid white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            position: 'absolute',
                            bottom: `${height}px`,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 3,
                          }}
                        >
                          {/* Value label */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '-25px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              fontSize: '10px',
                              color: '#374151',
                              fontWeight: '600',
                              backgroundColor: 'white',
                              padding: '2px 4px',
                              borderRadius: '3px',
                              border: '1px solid #e5e7eb',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {week.entitled_users}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Week labels */}
                <div style={{ display: 'flex', marginTop: '10px', paddingLeft: '10px' }}>
                  {entitledUsersData.map((week, index) => (
                    <div
                      key={index}
                      style={{
                        flex: 1,
                        fontSize: '11px',
                        color: '#666',
                        textAlign: 'center',
                      }}
                    >
                      {week.week.split('-')[1]}/{week.week.split('-')[2]}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                <div>Avg Growth: {safeToFixed(metrics.weekly_entitled_users_trend?.average_growth_rate)}%</div>
                <div>Latest: {entitledUsersData[entitledUsersData.length - 1]?.entitled_users || 0} users</div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p>No data available</p>
            </div>
          )}
        </div>

        {/* Active T4W Trend - Bullet Chart */}
        <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
          <h3 className="text-xl font-semibold text-secondary-800 mb-4">Active T4W Trend</h3>
          {activeT4WData.length > 0 ? (
            <div>
              {/* Bullet chart - vertical list */}
              <div style={{ padding: '20px 0' }}>
                {[...activeT4WData].reverse().map((week: any, index) => {
                  const maxValue = Math.max(...activeT4WData.map((w) => w.active_t4w));
                  const bulletWidth = maxValue > 0 ? (week.active_t4w / maxValue) * 100 : 0;

                  // Find original index to compare with previous week
                  const originalIndex = activeT4WData.length - 1 - index;
                  const isDecline =
                    originalIndex > 0 && Number(week.active_t4w) < Number(activeT4WData[originalIndex - 1].active_t4w);

                  return (
                    <div key={index} style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                      {/* Week label */}
                      <div
                        style={{
                          width: '80px',
                          fontSize: '12px',
                          color: '#666',
                          textAlign: 'right',
                          marginRight: '12px',
                        }}
                      >
                        {week.week.split('-')[1]}/{week.week.split('-')[2]}
                      </div>

                      {/* Bullet container */}
                      <div
                        style={{
                          flex: 1,
                          height: '32px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          position: 'relative',
                          overflow: 'hidden',
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        {/* Background target line (average) */}
                        <div
                          style={{
                            position: 'absolute',
                            left: '60%',
                            top: '0',
                            bottom: '0',
                            width: '2px',
                            backgroundColor: '#d1d5db',
                            zIndex: 1,
                          }}
                        ></div>

                        {/* Actual bullet bar */}
                        <div
                          style={{
                            height: '100%',
                            width: `${Math.max(3, bulletWidth)}%`,
                            backgroundColor: isDecline ? '#ef4444' : '#2563eb',
                            borderRadius: '6px',
                            position: 'relative',
                            zIndex: 2,
                            transition: 'width 0.3s ease',
                          }}
                        >
                          {/* Bullet tip */}
                          <div
                            style={{
                              position: 'absolute',
                              right: '-4px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: '8px',
                              height: '8px',
                              backgroundColor: isDecline ? '#dc2626' : '#d97706',
                              borderRadius: '50%',
                              border: '2px solid white',
                            }}
                          ></div>
                        </div>

                        {/* Value label */}
                        <div
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '11px',
                            color: '#374151',
                            fontWeight: '600',
                            zIndex: 3,
                          }}
                        >
                          {week.active_t4w}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                <div>Latest T4W: {metrics.active_t4w_trend?.latest_t4w_count || 0} users</div>
                <div>Peak T4W: {Math.max(...activeT4WData.map((w) => w.active_t4w))} users</div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p>No data available</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Function to render data summary
  return (
    <div className="h-full flex flex-col space-y-6">
      {dataLoading || !selectedCustomer ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : customers.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-secondary-600">No customer data available</p>
        </div>
      ) : (
        <>
          <div className="mt-6 mx-4 bg-brand-gradient text-white p-6 rounded-qnav shadow-brand-card flex items-center justify-between">
            <h1 className="text-2xl font-display font-bold">Customer Metrics Dashboard</h1>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-primary-600 rounded"></div>
                <span className="text-sm text-white">Growth/Stable</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-error-500 rounded"></div>
                <span className="text-sm text-white">Decline</span>
              </div>
            </div>
          </div>

          {/* Customer Selection */}
          <div className="mx-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Select Customer</label>
                  <div className="relative">
                    <select
                      value={selectedCustomer}
                      onChange={handleCustomerChange}
                      className="min-w-[300px] pl-4 pr-10 py-3 text-base font-medium text-secondary-800 bg-white border-2 border-secondary-200 rounded-xl shadow-sm hover:border-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:ring-opacity-50 transition-all duration-200 appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={dataLoading}
                    >
                      {customers.map((customer) => (
                        <option key={customer} value={customer} className="py-2">
                          {customer === 'ALL_CUSTOMERS' ? 'All Customers (Consolidated)' : customer}
                        </option>
                      ))}
                    </select>
                    {/* Custom dropdown arrow */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Refresh Button */}
                <div className="pt-6">
                  <button
                    onClick={refreshMetrics}
                    disabled={dataLoading}
                    className="flex items-center px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <FiRefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Usage & Adoption Metrics Section */}
            {!dataLoading && metricsData && metricsData.metrics && (
              <div className="mb-6 mt-6 mx-4">
                <div className="flex gap-6">
                  {/* WoW Usage Metrics */}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-secondary-800 mb-4">WoW Usage Metrics</h3>
                    <div className="flex gap-4">
                      {/* Active Users */}
                      {(() => {
                        const growth = metricsData.metrics.weekly_active_users_growth?.latest_week_growth || 0;
                        const borderColor =
                          growth > 0 ? 'border-l-blue-500' : growth < 0 ? 'border-l-red-500' : 'border-l-gray-400';
                        const textColor = growth > 0 ? 'text-blue-600' : growth < 0 ? 'text-red-600' : 'text-gray-500';
                        const sign = growth > 0 ? '+' : growth < 0 ? '' : '';
                        return (
                          <div
                            className={`bg-white rounded-lg shadow-sm border border-secondary-100 ${borderColor} border-l-4 px-4 py-3 min-w-[140px]`}
                          >
                            <div className="text-xs font-medium text-secondary-600 mb-1">Active Users</div>
                            <div className="text-sm font-bold text-secondary-800">
                              {safeToLocaleString(
                                metricsData.metrics.weekly_active_users_growth?.recent_weeks_data?.slice(-1)[0]
                                  ?.active_users || 0,
                              )}
                            </div>
                            <div className={`text-xs font-medium ${textColor}`}>
                              {sign}
                              {safeToFixed(growth)}%
                            </div>
                          </div>
                        );
                      })()}

                      {/* Paid Users */}
                      {(() => {
                        const growth = metricsData.metrics.weekly_paid_users_trend?.latest_week_growth || 0;
                        const borderColor =
                          growth > 0 ? 'border-l-blue-500' : growth < 0 ? 'border-l-red-500' : 'border-l-gray-400';
                        const textColor = growth > 0 ? 'text-blue-600' : growth < 0 ? 'text-red-600' : 'text-gray-500';
                        const sign = growth > 0 ? '+' : growth < 0 ? '' : '';
                        return (
                          <div
                            className={`bg-white rounded-lg shadow-sm border border-secondary-100 ${borderColor} border-l-4 px-4 py-3 min-w-[140px]`}
                          >
                            <div className="text-xs font-medium text-secondary-600 mb-1">Paid Users</div>
                            <div className="text-sm font-bold text-secondary-800">
                              {safeToLocaleString(
                                metricsData.metrics.weekly_paid_users_trend?.recent_weeks_data?.slice(-1)[0]
                                  ?.paid_users || 0,
                              )}
                            </div>
                            <div className={`text-xs font-medium ${textColor}`}>
                              {sign}
                              {safeToFixed(growth)}%
                            </div>
                          </div>
                        );
                      })()}

                      {/* Entitled Users */}
                      {(() => {
                        const growth = metricsData.metrics.weekly_entitled_users_trend?.latest_week_growth || 0;
                        const borderColor =
                          growth > 0 ? 'border-l-blue-500' : growth < 0 ? 'border-l-red-500' : 'border-l-gray-400';
                        const textColor = growth > 0 ? 'text-blue-600' : growth < 0 ? 'text-red-600' : 'text-gray-500';
                        const sign = growth > 0 ? '+' : growth < 0 ? '' : '';
                        return (
                          <div
                            className={`bg-white rounded-lg shadow-sm border border-secondary-100 ${borderColor} border-l-4 px-4 py-3 min-w-[140px]`}
                          >
                            <div className="text-xs font-medium text-secondary-600 mb-1">Entitled Users</div>
                            <div className="text-sm font-bold text-secondary-800">
                              {safeToLocaleString(
                                metricsData.metrics.weekly_entitled_users_trend?.recent_weeks_data?.slice(-1)[0]
                                  ?.entitled_users || 0,
                              )}
                            </div>
                            <div className={`text-xs font-medium ${textColor}`}>
                              {sign}
                              {safeToFixed(growth)}%
                            </div>
                          </div>
                        );
                      })()}

                      {/* Active T4W */}
                      {(() => {
                        const growth =
                          metricsData.metrics.active_t4w_trend?.recent_weeks_data?.slice(-1)[0]?.growth_rate || 0;
                        const borderColor =
                          growth > 0 ? 'border-l-blue-500' : growth < 0 ? 'border-l-red-500' : 'border-l-gray-400';
                        const textColor = growth > 0 ? 'text-blue-600' : growth < 0 ? 'text-red-600' : 'text-gray-500';
                        const sign = growth > 0 ? '+' : growth < 0 ? '' : '';
                        return (
                          <div
                            className={`bg-white rounded-lg shadow-sm border border-secondary-100 ${borderColor} border-l-4 px-4 py-3 min-w-[140px]`}
                          >
                            <div className="text-xs font-medium text-secondary-600 mb-1">Active T4W</div>
                            <div className="text-sm font-bold text-secondary-800">
                              {safeToLocaleString(
                                metricsData.metrics.active_t4w_trend?.recent_weeks_data?.slice(-1)[0]?.active_t4w || 0,
                              )}
                            </div>
                            <div className={`text-xs font-medium ${textColor}`}>
                              {sign}
                              {safeToFixed(growth)}%
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Engagement Adoption Metrics */}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-secondary-800 mb-4">Engagement Adoption Metrics</h3>
                    <div className="flex gap-4">
                      {/* Paid/Entitled */}
                      {(() => {
                        const ratio = metricsData.metrics.p0_metrics?.paid_entitled_ratio || 0;
                        const isGood = ratio >= 65;
                        const borderColor = isGood ? 'border-l-green-500' : 'border-l-red-500';
                        const textColor = isGood ? 'text-green-600' : 'text-red-600';
                        return (
                          <div
                            className={`bg-white rounded-lg shadow-sm border border-secondary-100 ${borderColor} border-l-4 px-4 py-3 min-w-[140px]`}
                          >
                            <div className="text-xs font-medium text-secondary-600 mb-1">Paid/Entitled</div>
                            <div className={`text-sm font-bold ${textColor}`}>{safeToFixed(ratio)}%</div>
                            <div className="text-xs text-gray-500 mt-1">Target: ≥65%</div>
                          </div>
                        );
                      })()}

                      {/* Active/Paid */}
                      {(() => {
                        const ratio = metricsData.metrics.p0_metrics?.active_paid_ratio || 0;
                        const isGood = ratio >= 75;
                        const borderColor = isGood ? 'border-l-green-500' : 'border-l-red-500';
                        const textColor = isGood ? 'text-green-600' : 'text-red-600';
                        return (
                          <div
                            className={`bg-white rounded-lg shadow-sm border border-secondary-100 ${borderColor} border-l-4 px-4 py-3 min-w-[140px]`}
                          >
                            <div className="text-xs font-medium text-secondary-600 mb-1">Active/Paid</div>
                            <div className={`text-sm font-bold ${textColor}`}>{safeToFixed(ratio)}%</div>
                            <div className="text-xs text-gray-500 mt-1">Target: ≥75%</div>
                          </div>
                        );
                      })()}

                      {/* Entitled/Eligible */}
                      {(() => {
                        const ratio = metricsData.metrics.p0_metrics?.entitled_eligible_ratio || 0;
                        const eligibleCount = metricsData.metrics.p0_metrics?.totals?.eligible || 0;
                        const isNA = eligibleCount === 0;
                        const isGood = ratio >= 80;
                        const borderColor = isNA
                          ? 'border-l-gray-400'
                          : isGood
                            ? 'border-l-green-500'
                            : 'border-l-red-500';
                        const textColor = isNA ? 'text-secondary-500' : isGood ? 'text-green-600' : 'text-red-600';
                        return (
                          <div
                            className={`bg-white rounded-lg shadow-sm border border-secondary-100 ${borderColor} border-l-4 px-4 py-3 min-w-[140px]`}
                          >
                            <div className="text-xs font-medium text-secondary-600 mb-1">Entitled/Eligible</div>
                            <div className={`text-sm font-bold ${textColor}`}>
                              {isNA ? 'NA' : `${safeToFixed(ratio)}%`}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Target: ≥80%</div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {dataLoading && (
              <div className="bg-surface rounded-qnav shadow-brand-card p-8 border border-secondary-200 text-center">
                <div className="qnav-loading-spinner border-primary-600 mx-auto mb-4"></div>
                <h3 className="text-xl font-semibold text-secondary-800 mb-2">Loading Metrics Data</h3>
                <p className="text-secondary-600">Please wait while we fetch the latest metrics data...</p>
              </div>
            )}

            {/* Metrics Display */}
            {!dataLoading && metricsData && (
              <>
                {/* This Week's Trends for All Customers */}
                {renderExecutiveSummary()}

                {/* Charts */}
                {renderCharts()}
              </>
            )}

            {/* No Data State */}
            {!dataLoading && !metricsData && (
              <div className="bg-surface rounded-qnav shadow-brand-card p-8 border border-secondary-200 text-center">
                <div className="text-secondary-400 mb-4">
                  <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-secondary-800 mb-2">No Customer Data Available</h3>
                <p className="text-secondary-600 mb-6">
                  Upload a customer data file to visualize metrics and generate insights
                </p>
              </div>
            )}
          </div>

          {/* Data Last Updated Section - Bottom of Page */}
          {selectedCustomer === 'ALL_CUSTOMERS' && customerPerformance.length > 0 && (
            <div className="mt-8 text-right border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Data Last Updated:</span>{' '}
                {customerPerformance[0].lastUpdated
                  ? new Date(customerPerformance[0].lastUpdated).toLocaleString('en-US', {
                      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                      timeZoneName: 'short',
                    })
                  : 'N/A'}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QNavCustomerMetrics;
export { QNavCustomerMetrics };
